import { useEffect, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { ReferralRow } from '../../components/ReferralRow';
import { Spinner } from '../../components/Spinner';
import { apiGet, ClientApiError } from '../../api/client';
import type { Referral, ReferralStatus } from '../../../shared/types';

const TABS: { key: ReferralStatus; label: string }[] = [
  { key: 'submitted', label: 'New' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'recon_attached', label: 'Recon' },
  { key: 'complete', label: 'Complete' },
  { key: 'declined', label: 'Declined' },
];

export function QueueScreen() {
  const [tab, setTab] = useState<ReferralStatus>('submitted');
  const [referrals, setReferrals] = useState<Referral[] | null>(null);
  const [newCount, setNewCount] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setReferrals(null);
    setError('');
    apiGet<{ referrals: Referral[] }>(`/api/referrals?status=${tab}`)
      .then((data) => setReferrals(data.referrals))
      .catch((caught) =>
        setError(caught instanceof ClientApiError ? caught.message : 'Could not load the queue.'),
      );
  }, [tab]);

  // The badge on New is the count of unassigned referrals waiting on dispatch.
  // Refetched whenever the tab changes so it stays honest after an assignment.
  useEffect(() => {
    apiGet<{ referrals: Referral[] }>('/api/referrals?status=submitted')
      .then((data) => setNewCount(data.referrals.length))
      .catch(() => undefined);
  }, [tab]);

  return (
    <AppShell title="Dispatch Queue">
      <div
        style={{
          display: 'flex',
          gap: '0.4rem',
          overflowX: 'auto',
          paddingBottom: '0.5rem',
          marginBottom: '0.5rem',
        }}
      >
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            aria-current={tab === entry.key ? 'page' : undefined}
            onClick={() => setTab(entry.key)}
            style={{
              flex: '0 0 auto',
              minHeight: 'var(--tap)',
              padding: '0 0.8rem',
              borderRadius: 999,
              border: '1px solid var(--line)',
              background: tab === entry.key ? 'var(--navy)' : 'var(--surface)',
              color: tab === entry.key ? '#fff' : 'var(--muted)',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            {entry.label}
            {entry.key === 'submitted' && newCount ? (
              <span
                style={{
                  marginLeft: '0.4rem',
                  padding: '0 0.4rem',
                  borderRadius: 999,
                  background: tab === 'submitted' ? 'var(--azure)' : 'var(--danger)',
                  color: '#fff',
                  fontSize: '0.75rem',
                }}
              >
                {newCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}
      {!referrals && !error && <Spinner label="Loading the queue" />}
      {referrals?.length === 0 && <p className="muted">Nothing here right now.</p>}
      {referrals?.map((referral) => (
        <ReferralRow
          key={referral.id}
          referral={referral}
          to={`/dispatch/referrals/${referral.id}`}
        />
      ))}
    </AppShell>
  );
}
